import React from 'react'
import { Text, View } from 'react-native'
import {
  RoundCheckBlue,
  ErrorRed,
  IconCloseCross,
  CardIconBuilder,
  BankIcon,
} from '../../assets/icons'
import { Spacer } from '../atoms'
import { ListItem } from '../atoms/builder-2.0/ListItem'
import SubmitButton from '../../screens/Auth/SubmitButton'
import numbro from 'numbro'
import { useInvoiceDetailsContext } from '../../screens/Contractor/TabInvoice/InvoiceDetailsContext'
import { routes } from '@linqpal/models'
import { GetIcon } from '../atoms/GetIcon'
import { useNavigation } from '@react-navigation/native'
import { paths } from '../../screens/links'
import SModal from '../molecules/SModal'
import { Invoice } from '../../screens/Contractor/InvoicesTab/Invoice/InvoiceDetails'
import { useStore } from '../../store'
import { useTranslation } from 'react-i18next'

const renderHeaderColor = (status) => {
  switch (status) {
    case 'loading':
      return '#F4F9FD'
    case 'success':
      return 'white'
    case 'Insufficient Funds':
      return '#f5ebec'
    default:
      return '#f5ebec'
  }
}

const HeaderIcon = ({ status }) => (
  <>
    {(status === 'loading' || status === 'success') && (
      <RoundCheckBlue style={{ alignSelf: 'center' }} />
    )}
    {status === 'Insufficient Funds' && (
      <ErrorRed style={{ alignSelf: 'center' }} />
    )}
    {status !== 'Insufficient Funds' &&
      status !== 'loading' &&
      status !== 'success' && <ErrorRed style={{ alignSelf: 'center' }} />}
  </>
)

const renderTitle = (status) => {
  return (
    <>
      {(status === 'loading' || status === 'success') &&
        'Payment is Processing'}
      {status === 'Insufficient Funds' && 'Insufficient funds'}
      {status === 'cancelled' &&
        'This invoice has been cancelled by the supplier'}
      {status === 'fail' && 'Unsuccessful'}
      {status === 'Error' && 'Error'}
      {status === 'LogicalError' && 'There has been some error'}
      {status !== 'loading' &&
        status !== 'success' &&
        status !== 'Insufficient Funds' &&
        status !== 'cancelled' &&
        status !== 'fail' &&
        status !== 'Error' &&
        status !== 'LogicalError' &&
        'Please Try Again'}
    </>
  )
}

const Message = ({ invoice, status, message = '', totalAmount }) => (
  <Text
    style={{
      textAlign: 'center',
      marginHorizontal: 30,
      paddingBottom: 45,
      fontSize: 14,
      lineHeight: 21,
      color: '#668598',
    }}
  >
    {(status === 'loading' || status === 'success') &&
      `Your payment of $${numbro(totalAmount).format({
        thousandSeparated: true,
        mantissa: 2,
      })} to ${invoice.company.name} is processing`}
    {status === 'Insufficient Funds' &&
      'Your account does not have sufficient funds'}
    {status === 'cancelled' && 'This invoice has been cancelled'}
    {status === 'fail' &&
      'This payment may not be successful, please check back later'}
    {status === 'Error' && (message || 'There was some error')}
    {status === 'LogicalError' && (message || 'There was some error')}
    {status !== 'loading' &&
      status !== 'success' &&
      status !== 'Insufficient Funds' &&
      status !== 'cancelled' &&
      status !== 'fail' &&
      status !== 'Error' &&
      status !== 'LogicalError' &&
      'System failure'}
  </Text>
)

const ButtonMessage = ({ status }) => {
  return (
    <Text style={{ fontWeight: '500' }}>
      {(status === 'loading' || status === 'success') && 'Done'}
      {status === 'Insufficient Funds' && 'Change payment method'}
      {status === 'cancelled' && 'Go back to invoice'}
      {status === 'fail' && 'Show payment method'}
      {status !== 'loading' &&
        status !== 'success' &&
        status !== 'Insufficient Funds' &&
        status !== 'cancelled' &&
        status !== 'fail' &&
        'Go back and try again'}
    </Text>
  )
}

export const InvoicePaymentStatusModal = ({
  invoice,
  totalAmount,
}: {
  invoice?: Invoice
  totalAmount: number
}) => {
  const navigation: any = useNavigation()
  const {
    screensStore: { paymentMethodsStore },
  } = useStore()
  const { t } = useTranslation('global')
  const {
    invoicePaymentStatus,
    invoiceStatusModalVisibility,
    setInvoiceStatusModalVisibility,
    invoicePaymentMethod,
    setAddToProjectModalVisibility,
    setProjectSelectionModalVisibility,
    setFoundProject,
  } = useInvoiceDetailsContext()

  async function onSubmit() {
    if (
      invoicePaymentStatus.status === 'loading' ||
      invoicePaymentStatus.status === 'success'
    ) {
      if (invoice) {
        const { project } = await routes.projects.match({
          address: invoice.address,
          unitNumber: invoice.unitNumber,
          city: invoice.city,
          state: invoice.state,
          zip: invoice.zip,
        }) //returns project id and project name
        if (project) {
          setFoundProject(project)
          setInvoiceStatusModalVisibility(false)
          setAddToProjectModalVisibility(true)
        } else {
          setInvoiceStatusModalVisibility(false)
          setProjectSelectionModalVisibility(true)
        }
      }
    } else {
      setInvoiceStatusModalVisibility(false)
    }
  }

  const onClose = () => {
    setInvoiceStatusModalVisibility(false)
    navigation.replace(paths.Home._self, {
      screen: paths.Console.Payables._self,
      params: { screen: paths.Console.Payables.home },
    })
  }

  return (
    <SModal
      visible={invoiceStatusModalVisibility}
      modalStyle={{ justifyContent: 'flex-end' }}
      onClose={onClose}
      contentStyle={{ padding: 0 }}
      containerStyle={{
        backgroundColor:
          invoice && renderHeaderColor(invoicePaymentStatus.status),
        width: 355,
        borderRadius: 20,
        marginHorizontal: 10,
        padding: 0,
        display: 'flex',
        justifyContent: 'flex-start',
      }}
    >
      <View style={{ height: 40, alignItems: 'flex-end', padding: 15 }}>
        {['success'].includes(invoicePaymentStatus.status) || (
          <GetIcon
            icon={<IconCloseCross height="14" width="14" />}
            onPress={onClose}
          />
        )}
      </View>
      {invoice && (
        <>
          <HeaderIcon status={invoicePaymentStatus.status} />
          <Spacer height={16} />
          <View style={{ alignItems: 'center' }}>
            <Text
              style={{
                textAlign: 'center',
                fontSize: 18,
                fontWeight: '600',
                color: '#003353',
              }}
            >
              {renderTitle(invoicePaymentStatus.status)}
            </Text>
            <Spacer height={12} />
            <Message
              invoice={invoice}
              status={invoicePaymentStatus.status}
              totalAmount={totalAmount}
            />
          </View>
          <View
            style={{
              backgroundColor: 'white',
              borderBottomLeftRadius: 20,
              borderBottomRightRadius: 20,
              paddingTop: 32,
              paddingHorizontal: 20,
            }}
          >
            <ListItem
              key={0}
              icon={
                invoicePaymentMethod?.paymentMethodType === 'card' ? (
                  <CardIconBuilder />
                ) : (
                  <BankIcon />
                )
              }
              title={invoicePaymentMethod?.name}
              subtitle={paymentMethodsStore.getPaymentMethodSubtitle(
                t,
                invoicePaymentMethod,
              )}
              endIcon={<></>}
            />
            {['success'].includes(invoicePaymentStatus.status) || (
              <SubmitButton
                style={{ backgroundColor: '#00A0F3' }}
                onPress={onSubmit}
              >
                <ButtonMessage status={invoicePaymentStatus.status} />
              </SubmitButton>
            )}
            <Spacer height={20} />
          </View>
        </>
      )}
    </SModal>
  )
}
